import { Outlet, useOutletContext, useParams, useSearchParams } from "react-router-dom"
import { useEffect } from "react"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"

import BusinessEntityTree from '../../components/BusinessEntityTree'
import Header from "../../components/Header"
import Notification from "../../components/Notification"

import useBusinessEntityData from '../../hooks/useBusinessEntityData'
import useUserManifest from "../../hooks/useUserManifest.js"

import { hasPermission } from '../../utils/utils'
import { UserPermissions } from '../../utils/constants'

import { showSidebarSignal, debugSignal } from "../../state/signals/userSettingsSignals.js"
import { updateDebug, updateSelectedBusinessEntityId, updateShowSidebar } from "../../state/actions/userSettingsActions.js"
import DebugAccordion from "../../components/DebugAccordion.jsx"

import { debugDataSignal } from "../../state/signals/debugSignals.js"
import { addDebugData } from "../../state/actions/debugActions.js"

export default function BusinessEntity() {

    const { beId } = useParams()
    const [searchParams] = useSearchParams()

    const { user, config, accessToken, permissionMasks, permissions } = useOutletContext()
    const { businessEntitiesSignal } = useBusinessEntityData(user.businessEntityId, config, accessToken)
    const { userManifestSignal } = useUserManifest(beId, config, accessToken)

    const currentUrlFragmentSignal = useSignal('')

    const uniquePermissionMasksBusinessEntityIds = useComputed(() => {
        const roleBusinessEntityIds = permissionMasks.map(pm => pm.roleBusinessEntityIds)
        const flattened = roleBusinessEntityIds.flat()
        const uniqueSet = new Set(flattened)
        const unique = [...uniqueSet]
        return unique
    })

    useEffect(() => {
        // this will store in local storage in case the user goes back to legacy site
        updateSelectedBusinessEntityId(beId)
    }, [beId])

    useEffect(() => {
        const hasDebugParam = !!searchParams.get('debug')
        if (hasDebugParam) {
            updateDebug(hasDebugParam)
        }
    }, [searchParams])

    useSignalEffect(() => {
        const currentPathName = location.pathname
        const match = currentPathName.match(/\/be\/(\d+)\/(\w+)/);
        currentUrlFragmentSignal.value = match ? match[2] : ''
        // console.log('currentBusinessEntityId', currentBusinessEntityId.value)
        // console.log('currentUrlFragment', currentUrlFragment.value)


    })

    const userPermissionsSignal = useComputed(() => {
        if (businessEntitiesSignal.value.length) {
            // walk up the business entity ancestry to get a list of all be ids
            let ancestorBEIds = []
            let current = businessEntitiesSignal.value.find(element => element.id == beId)

            // handle if assigned to shift4 root
            if (current) {
                ancestorBEIds.push(current.id)
            }
            while(current && current.parentId != 0) {
                current = businessEntitiesSignal.value.find(element => element.id == current.parentId)
                if (current) {
                    ancestorBEIds.push(current.id)
                }
            }

            // loop over all permissions and build a Map indexed by permission key. The value will be an object with default access flags
            const permissionsMap = new Map()
            permissions.forEach(p => {
                // default to denied
                let userPermission = {
                    id: p.id,
                    key: p.key,
                    name: p.name,
                    description: p.description,
                    canView: false,
                    canEdit: false,
                    canDelete: false,
                    isEnabled: false,
                    canGrant: false
                }
                let current = permissionsMap.get(p.key)
                userPermission = current ? current : userPermission

                // add/update the Map
                permissionsMap.set(p.key, userPermission)
            })

            // update permissionsMap as needed to reflect permissions that are enabled
            permissionsMap.forEach(p => {
                const masks = permissionMasks.filter(item => item.permissionId == p.id)

                masks.forEach(m => {
                    ancestorBEIds.forEach(ancestorBEId => {
                        if (m.roleBusinessEntityIds.includes(ancestorBEId)) {
                            // the selected be or its ancestor is in the current permissionMask
                            // update the flags in our permissionMask if any are enabled
                            if (m.canView) {
                                p.canView = true
                            }
                            if (m.canEdit) {
                                p.canEdit = true
                            }
                            if (m.canDelete) {
                                p.canDelete = true
                            }
                            if (m.isEnabled) {
                                p.isEnabled = true
                            }
                            if (m.canGrant) {
                                p.canGrant = true
                            }
                        }
                    })
                })

            })

            return [...permissionsMap.values()]
        }
        return []
    })


    const userPermissionsSummary = useComputed(() => {
        // userPermissionsSummary is a high level permission structure that navigation and most components can use to control the UI visibility. If a component needs more granular permissions
        // it can refer to the userPermissions array which contains all available permissions and actions values for the currently selected business entity
        if (userPermissionsSignal.value.length) {
            let summary = {
                canViewUtgs: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canView)),
                canEditUtgs: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canEdit)),
                
                canViewConfigTemplates: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canView)),
                canEditConfigTemplates: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgServices', UserPermissions.canEdit)),

                canViewTasks: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Machines', UserPermissions.canView)),
                canEditTasks: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Machines', UserPermissions.canEdit)),

                canViewDevices: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Devices', UserPermissions.canView)),
                canEditDevices: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:Devices', UserPermissions.canEdit)),

                canViewForms: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:DeviceForms', UserPermissions.canView)),
                canEditForms: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:DeviceForms', UserPermissions.canEdit)),

                canViewProfiles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgConfigurations', UserPermissions.canView)),
                canEditProfiles: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgConfigurations', UserPermissions.canEdit)),

                canViewInstallations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgInstallations', UserPermissions.canView)),
                canEditInstallations: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'In4m:UtgInstallations', UserPermissions.canEdit)),

                canViewUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Users', UserPermissions.canView)),
                canEditUsers: (!!userPermissionsSignal.value.length && hasPermission(userPermissionsSignal.value, 'Users', UserPermissions.canEdit))
            }
            //DEBUG
            // overwrite the correct values for debugging page access
            // summary.canViewUtgs = true
            // summary.canEditUtgs = false
            // summary.canViewConfigTemplates = true
            // summary.canEditConfigTemplates = true
            // summary.canViewTasks = true
            // summary.canEditTasks = true
            // summary.canViewDevices = true
            // summary.canEditDevices = true
            // summary.canViewForms = true
            // summary.canEditForms = true
            // summary.canViewProfiles = true
            // summary.canEditProfiles = true
            // summary.canViewInstallations = true
            // summary.canEditInstallations = true
            // summary.canViewUsers = true
            // summary.canEditUsers = false

            return summary
        }

    })

    const accordionData = [
        { name: 'businessEntities', data: businessEntitiesSignal.value},
        { name: 'currentBusinessEntityId', data: beId },
        { name: 'currentUrlFragment', data: currentUrlFragmentSignal.value },
        { name: 'showSidebarSignal', data: showSidebarSignal.value },
        { name: 'Permissions', data: permissions },
        { name: 'PermisionMasks', data: permissionMasks },
        { name: 'User', data: user },
        { name: 'User Permissions', data: userPermissionsSignal.value },
        { name: 'User Permissions Summary', data: userPermissionsSummary },
        { name: 'uniquePermissionMasksBusinessEntityIds', data: uniquePermissionMasksBusinessEntityIds}
    ]

    return (
        <>
            <div className="vh-100">
                <Header config={config} userManifestSignal={userManifestSignal} userPermissions={userPermissionsSignal.value} userPermissionsSummary={userPermissionsSummary.value} />
                <div style={{ height: "calc(100vh - 58px)" }}>
                    <aside className="sidebar bg-light" data-show-tree={showSidebarSignal.value ? true : undefined}>
                        <div className="sidebar-content">
                            {  user && user.businessEntityId &&
                                beId &&
                                (<BusinessEntityTree key={beId} businessEntitiesSignal={businessEntitiesSignal} currentBusinessEntityId={beId} currentUrlFragment={currentUrlFragmentSignal.value} rootBusinessEntityId={user.businessEntityId} permissionMasks={permissionMasks} showTreeSignal={showSidebarSignal} handleToggleTree={updateShowSidebar}></BusinessEntityTree>)}
                        </div>
                    </aside>
                    <main className="main-content">
                        <div className="main-scroll-content">
                            <Notification />
                            <Outlet context={{ config, accessToken, user, userPermissions: userPermissionsSignal.value, userPermissionsSummary: userPermissionsSummary, currentUrlFragment: currentUrlFragmentSignal.value, currentBusinessEntityId: beId, accordionData }} />
                        </div>
                    </main>
                </div>
            </div>
        </>
    )
}